import type { CabinetPieceKindValue } from './schemas/project-cabinets';

/**
 * Cut-list ("Pe piese") cabinet geometry, shared by the API service and the web
 * dialog.
 *
 * A PIECES cabinet has no typed H/W/D — the user fills in a table of pieces and
 * the overall dimensions fall out of it. The dialog shows those derived numbers
 * live above the table while `CabinetsService` persists the same numbers into
 * `Cabinet.{width,height,depth}Mm`, so list views, the offer PDF and BOM keep
 * working against the parametric columns unchanged. Both sides must agree, so
 * the derivation lives here rather than being written twice.
 */

export interface DerivableCabinetPiece {
  kind: CabinetPieceKindValue;
  lengthMm: number;
  widthMm: number;
}

export interface DerivedCabinetDimensions {
  widthMm: number;
  heightMm: number;
  depthMm: number;
}

/**
 * The six rows a new cut list is seeded with, in display order.
 *
 * A convenience only — none of them is required. Every row can be deleted and
 * any kind can be added back, so a cabinet may legitimately have (say) two
 * fronts and no sides at all.
 */
export const DEFAULT_CABINET_PIECE_KINDS = [
  'SIDE_LEFT',
  'SIDE_RIGHT',
  'SIDE_UPPER',
  'SIDE_LOWER',
  'BACK',
  'FRONT',
] as const satisfies readonly CabinetPieceKindValue[];

/**
 * Canonical display order of the kinds: sides top-to-bottom, then the faces,
 * then the internals. New rows are slotted into this order so a cut list stays
 * grouped by kind — see {@link cabinetPieceInsertIndex}.
 */
export const CABINET_PIECE_KIND_ORDER = [
  'SIDE_LEFT',
  'SIDE_RIGHT',
  'SIDE_UPPER',
  'SIDE_LOWER',
  'BACK',
  'FRONT',
  'SHELF',
  'DIVIDER',
  'CUSTOM',
] as const satisfies readonly CabinetPieceKindValue[];

/**
 * Every kind the "add row" control offers, in display order.
 *
 * Same list as {@link CABINET_PIECE_KIND_ORDER} by design: anything you can add
 * has a defined slot to be added into.
 */
export const ADDABLE_CABINET_PIECE_KINDS = CABINET_PIECE_KIND_ORDER;

/**
 * Where a newly added row of `kind` belongs in the current list.
 *
 * Keeps rows grouped without ever reshuffling what the user already has:
 *  - if rows of that kind exist, the new one goes directly after the last of
 *    them (so "Latura stânga 2" lands under "Latura stânga 1");
 *  - otherwise it goes before the first row that sorts after it in
 *    {@link CABINET_PIECE_KIND_ORDER}, falling back to the end.
 *
 * Existing rows are deliberately left where they are — a cut list loaded from
 * the database keeps its stored `orderIndex` rather than being re-sorted under
 * the user.
 */
export function cabinetPieceInsertIndex(
  kinds: readonly CabinetPieceKindValue[],
  kind: CabinetPieceKindValue,
): number {
  for (let i = kinds.length - 1; i >= 0; i -= 1) {
    if (kinds[i] === kind) return i + 1;
  }
  const rank = (k: CabinetPieceKindValue) => CABINET_PIECE_KIND_ORDER.indexOf(k);
  const newRank = rank(kind);
  for (let i = 0; i < kinds.length; i += 1) {
    if (rank(kinds[i]!) > newRank) return i;
  }
  return kinds.length;
}

/**
 * `Cabinet.{width,height,depth}Mm` are non-null Ints, so every branch must yield
 * a number — 0 is the "nothing filled in yet" value and is what `dimMm` in
 * project-cabinets.ts allows.
 */
const clampDim = (n: number): number =>
  Number.isFinite(n) ? Math.max(0, Math.min(100_000, Math.trunc(n))) : 0;

/**
 * Overall cabinet dimensions implied by a cut list:
 *   height = left-side length, depth = left-side width, width = upper-side length.
 *
 * Each falls back to the other rows that carry the same dimension, so a cabinet
 * whose left side has not been filled in yet still reports a sensible gabarit
 * instead of collapsing to 0.
 */
export function deriveCabinetDimensionsFromPieces(
  pieces: ReadonlyArray<DerivableCabinetPiece>,
): DerivedCabinetDimensions {
  const first = (kind: CabinetPieceKindValue) => pieces.find((p) => p.kind === kind);
  const left = first('SIDE_LEFT');
  const right = first('SIDE_RIGHT');
  const upper = first('SIDE_UPPER');
  const lower = first('SIDE_LOWER');
  const back = first('BACK');
  const front = first('FRONT');

  const pick = (...candidates: Array<number | undefined>): number =>
    clampDim(candidates.find((v) => typeof v === 'number' && v > 0) ?? 0);

  return {
    // Sides and faces run full height along their length.
    heightMm: pick(left?.lengthMm, right?.lengthMm, back?.lengthMm, front?.lengthMm),
    // Depth is the narrow dimension of every horizontal/vertical side panel.
    depthMm: pick(left?.widthMm, right?.widthMm, upper?.widthMm, lower?.widthMm),
    // Width spans the top/bottom panels, and is the narrow dimension of a face.
    widthMm: pick(upper?.lengthMm, lower?.lengthMm, back?.widthMm, front?.widthMm),
  };
}

export interface CabinetPieceEdgeCounts {
  edgeCountLength: number;
  edgeCountWidth: number;
}

/**
 * Default edge banding per kind, reverse-engineered from the parametric
 * calculator so a cut list built from the seeded rows prices like the equivalent
 * PARAMETRIC cabinet. Terms quoted from `cabinet.calculator.ts`:
 *
 *   carcass  4·(H+W+D)   the four side panels together
 *   shelf    W           front lip only
 *   divider  W+H
 *   front    2·(H+W)     full perimeter
 *   back     2·(H+W)     but only when `backHasEdge` — see below
 *
 * The carcass term splits exactly across its four panels: left and right are
 * H×D and take 2·H + D each, upper and lower are W×D and take 2·W + D each,
 * summing to 4H + 4W + 4D. The `cabinet-pieces-parity` spec pins that, and the
 * matching material areas, against the calculator itself.
 *
 * BACK is 0/0 rather than the perimeter because the dialog has always submitted
 * `backHasEdge: false` — backs are PFL and carry no banding. Set the counts by
 * hand on the row for the rare cabinet that needs them.
 */
export const CABINET_PIECE_DEFAULT_EDGE_COUNTS: Readonly<
  Record<CabinetPieceKindValue, CabinetPieceEdgeCounts>
> = {
  SIDE_LEFT: { edgeCountLength: 2, edgeCountWidth: 1 },
  SIDE_RIGHT: { edgeCountLength: 2, edgeCountWidth: 1 },
  SIDE_UPPER: { edgeCountLength: 2, edgeCountWidth: 1 },
  SIDE_LOWER: { edgeCountLength: 2, edgeCountWidth: 1 },
  BACK: { edgeCountLength: 0, edgeCountWidth: 0 },
  FRONT: { edgeCountLength: 2, edgeCountWidth: 2 },
  SHELF: { edgeCountLength: 1, edgeCountWidth: 0 },
  DIVIDER: { edgeCountLength: 1, edgeCountWidth: 1 },
  // No parametric analogue — the user says what a custom piece needs.
  CUSTOM: { edgeCountLength: 0, edgeCountWidth: 0 },
};

/**
 * Which of the cabinet's three dimensions a piece's own two run along.
 *
 * A panel is flat, so it always spans exactly two of the box's H/W/D — and which
 * two is what separates a left side (height × depth) from an upper side (width ×
 * depth), even though both read as plain "Lungime × Lățime" in the cut list.
 * Naming them is what stops a row's `Lățime` being read as the cabinet's own
 * `Lățime`, which is a different number on every side panel.
 *
 * Load-bearing: this is the same decomposition {@link cabinetPiecesFromParametric}
 * seeds the rows with, restated so the table can name it. A test pins the two
 * together — an annotation that disagreed with the geometry would be worse than
 * no annotation at all.
 *
 * CUSTOM is free-form and spans nothing in particular, hence null.
 */
export type CabinetDimensionAxis = 'HEIGHT' | 'WIDTH' | 'DEPTH';

export interface CabinetPieceAxes {
  lengthAxis: CabinetDimensionAxis | null;
  widthAxis: CabinetDimensionAxis | null;
}

export const CABINET_PIECE_AXES: Readonly<Record<CabinetPieceKindValue, CabinetPieceAxes>> = {
  SIDE_LEFT: { lengthAxis: 'HEIGHT', widthAxis: 'DEPTH' },
  SIDE_RIGHT: { lengthAxis: 'HEIGHT', widthAxis: 'DEPTH' },
  SIDE_UPPER: { lengthAxis: 'WIDTH', widthAxis: 'DEPTH' },
  SIDE_LOWER: { lengthAxis: 'WIDTH', widthAxis: 'DEPTH' },
  BACK: { lengthAxis: 'HEIGHT', widthAxis: 'WIDTH' },
  FRONT: { lengthAxis: 'HEIGHT', widthAxis: 'WIDTH' },
  SHELF: { lengthAxis: 'WIDTH', widthAxis: 'DEPTH' },
  DIVIDER: { lengthAxis: 'HEIGHT', widthAxis: 'WIDTH' },
  CUSTOM: { lengthAxis: null, widthAxis: null },
};

/** The two cabinet dimensions a row of `kind` spans. */
export function cabinetPieceAxes(kind: CabinetPieceKindValue): CabinetPieceAxes {
  return CABINET_PIECE_AXES[kind];
}

/** The edge counts a freshly added row of `kind` starts with. */
export function defaultPieceEdgeCounts(kind: CabinetPieceKindValue): CabinetPieceEdgeCounts {
  return CABINET_PIECE_DEFAULT_EDGE_COUNTS[kind];
}

// ----- Parametric → cut list ("Switch to pieces") -----

/**
 * A PARAMETRIC cabinet, as far as its equivalent cut list is concerned.
 *
 * Mirrors the Custom tab's geometry fields. Materials are deliberately absent:
 * a row only knows which of the form's three material *slots* it takes, and the
 * caller resolves those (see {@link SeededPieceMaterialSlot}).
 */
export interface ParametricCabinetSpec {
  widthMm: number;
  heightMm: number;
  depthMm: number;
  shelvesCount: number;
  dividersCount: number;
  carcassDoubled: boolean;
  shelvesDoubled: boolean;
  dividersDoubled: boolean;
  /** A FRONT row is emitted only when a front material is picked. */
  hasFront: boolean;
  /** Likewise BACK. */
  hasBack: boolean;
}

/**
 * The most rows {@link cabinetPiecesFromParametric} can ever emit: four sides,
 * the two faces, and one counted row each for the shelves and the dividers.
 * Comfortably under `MAX_CABINET_PIECES`, which is what lets the generator skip
 * a size check entirely.
 */
export const MAX_SEEDED_CABINET_PIECES = 8;

/** Which of the parametric form's three material slots a seeded row takes. */
export type SeededPieceMaterialSlot = 'CARCASS' | 'FRONT' | 'BACK';

/** One generated cut-list row: geometry only, no label and no material id. */
export interface SeededCabinetPiece extends CabinetPieceEdgeCounts {
  kind: CabinetPieceKindValue;
  lengthMm: number;
  widthMm: number;
  /** Identical boards this row stands for — the shelf/divider count. */
  quantity: number;
  doubled: boolean;
  materialSlot: SeededPieceMaterialSlot;
}

/**
 * Normalise a number that reached us from a form.
 *
 * Callers on the web side read these off `useForm().getValues()`, which returns
 * the *raw* input values — so a field the schema types as `number` via
 * `z.coerce.number()` is really the string "600" until the resolver runs on
 * submit. Coercing here keeps {@link SeededCabinetPiece} honest about its own
 * types rather than handing back rows whose `lengthMm` is secretly a string.
 */
const toInt = (value: number): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
};

const clampCount = (n: number): number => Math.max(0, toInt(n));

/**
 * The cut list equivalent to a parametric cabinet — what "Switch to pieces" on
 * the Custom tab generates.
 *
 * The geometry is not a fresh invention: every row reproduces the closed-form
 * term `cabinet.calculator.ts` prices for that part of the carcass, so the two
 * descriptions cost the same. `cabinet-pieces-parity.spec.ts` pins that against
 * the calculators themselves.
 *
 *   SIDE_LEFT / SIDE_RIGHT   H×D    together with the two below they reproduce
 *   SIDE_UPPER / SIDE_LOWER  W×D    the carcass term 2·(H·D + W·D) / 4·(H+W+D)
 *   BACK                     H×W    back term H·W, no banding (PFL)
 *   FRONT                    H×W    front term H·W / 2·(H+W)
 *   SHELF                    W×D    shelf term W·D / W, one row counted N
 *   DIVIDER                  H×W    divider term W·H / (W+H), one row counted N
 *
 * At most {@link MAX_SEEDED_CABINET_PIECES} rows come back however large the
 * shelf and divider counts are, because those collapse into one counted row
 * each — so the result always fits the API's `MAX_CABINET_PIECES` cap and needs
 * no pre-flight.
 *
 * Rows come back in {@link CABINET_PIECE_KIND_ORDER}, so the table stays grouped
 * by kind and {@link cabinetPieceInsertIndex} keeps slotting later additions
 * correctly. Feeding the result to {@link deriveCabinetDimensionsFromPieces}
 * recovers the H/W/D that went in.
 *
 * Two things do NOT round-trip identically, both inherent to the two pricing
 * models rather than to this mapping:
 *
 *   glassSanding — the parametric calculator marks up the FRONT only; the pieces
 *     calculator marks up every row whose material is glass-sanded. A cabinet
 *     whose *carcass* board is glass-sanded therefore prices higher as a cut
 *     list.
 *   C.E.F.       — `carcassExteriorFront` prices the carcass at the front
 *     material while leaving `materialId` stored, so BOM attributes the m² to
 *     the carcass board. A seeded row carries the front material outright, so
 *     BOM attributes it to the front. The price is unchanged, and the cut list
 *     then shows exactly what is billed — the same trade the table's existing
 *     "C.E.F." button already makes.
 */
export function cabinetPiecesFromParametric(spec: ParametricCabinetSpec): SeededCabinetPiece[] {
  const H = toInt(spec.heightMm);
  const W = toInt(spec.widthMm);
  const D = toInt(spec.depthMm);

  const rows: SeededCabinetPiece[] = [];
  const push = (
    kind: CabinetPieceKindValue,
    lengthMm: number,
    widthMm: number,
    quantity: number,
    doubled: boolean,
    materialSlot: SeededPieceMaterialSlot,
  ) => {
    rows.push({
      kind,
      lengthMm,
      widthMm,
      quantity,
      doubled,
      materialSlot,
      ...defaultPieceEdgeCounts(kind),
    });
  };

  push('SIDE_LEFT', H, D, 1, spec.carcassDoubled, 'CARCASS');
  push('SIDE_RIGHT', H, D, 1, spec.carcassDoubled, 'CARCASS');
  push('SIDE_UPPER', W, D, 1, spec.carcassDoubled, 'CARCASS');
  push('SIDE_LOWER', W, D, 1, spec.carcassDoubled, 'CARCASS');
  if (spec.hasBack) push('BACK', H, W, 1, false, 'BACK');
  if (spec.hasFront) push('FRONT', H, W, 1, false, 'FRONT');
  // Shelves and dividers run off the carcass material, exactly as the parametric
  // calculator prices them — and collapse into a single counted row each rather
  // than N identical ones, so `Polițe 3` on the Custom form reads back as one
  // "Poliță" row with a count of 3. `quantity` multiplies material AND banding,
  // which is precisely what N separate rows summed to.
  const shelves = clampCount(spec.shelvesCount);
  const dividers = clampCount(spec.dividersCount);
  if (shelves > 0) push('SHELF', W, D, shelves, spec.shelvesDoubled, 'CARCASS');
  if (dividers > 0) push('DIVIDER', H, W, dividers, spec.dividersDoubled, 'CARCASS');
  return rows;
}

/** Material area of one piece, in m². */
export function cabinetPieceAreaM2(piece: { lengthMm: number; widthMm: number }): number {
  return (piece.lengthMm / 1000) * (piece.widthMm / 1000);
}

/**
 * Edge banding of one piece, in linear metres.
 *
 * `edgeCountLength` counts how many of the two sides running along the *length*
 * are banded (so each contributes `lengthMm`), `edgeCountWidth` likewise for the
 * width. Must stay in lockstep with `cabinet-pieces.calculator.ts`.
 */
export function cabinetPieceEdgeM(piece: {
  lengthMm: number;
  widthMm: number;
  edgeCountLength: number;
  edgeCountWidth: number;
}): number {
  const clampCount = (n: number) =>
    Number.isFinite(n) ? Math.max(0, Math.min(2, Math.trunc(n))) : 0;
  return (
    (piece.lengthMm / 1000) * clampCount(piece.edgeCountLength) +
    (piece.widthMm / 1000) * clampCount(piece.edgeCountWidth)
  );
}
